import { useState, useCallback, useMemo } from 'react';
import { useCsvImportContext } from '../context/CsvImportContext';
import csvImportApi from '../utils/api';

/**
 * Custom hook for CSV import operations
 * Combines the context state with API functions
 *
 * @returns {Object} - CSV import operations and state
 */
export const useCsvImport = () => {
  const context = useCsvImportContext();
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  /**
   * Wrapper function to handle API calls with loading and error states
   *
   * @param {string} key - Operation key for tracking loading and error states
   * @param {Function} apiCall - API function to call
   * @param {Array} args - Arguments to pass to the API function
   * @returns {Promise<any>} - API response
   */
  const handleApiCall = useCallback(async (key, apiCall, ...args) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: null }));

    try {
      const result = await apiCall(...args);
      return result;
    } catch (error) {
      console.error(`API error in ${key}:`, error);
      setErrors(prev => ({ ...prev, [key]: error.message }));
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // Memoize the API functions to prevent unnecessary re-renders
  const apiOperations = useMemo(() => ({
    // Context state
    ...context,

    // Loading and error states
    loading,
    errors,

    // API operations with loading and error handling
    uploadCsvFile: (formData) =>
      handleApiCall('uploadCsvFile', csvImportApi.uploadCsvFile.bind(csvImportApi), formData),

    getCsvInfo: (sessionId) =>
      handleApiCall('getCsvInfo', csvImportApi.getCsvInfo.bind(csvImportApi), sessionId),

    suggestFieldMappings: (sessionId) =>
      handleApiCall('suggestFieldMappings', csvImportApi.suggestFieldMappings.bind(csvImportApi), sessionId),

    updateFieldMappings: (sessionId, mappings) =>
      handleApiCall('updateFieldMappings', csvImportApi.updateFieldMappings.bind(csvImportApi), sessionId, mappings),

    setDefaultValues: (sessionId, defaultValues) =>
      handleApiCall('setDefaultValues', csvImportApi.setDefaultValues.bind(csvImportApi), sessionId, defaultValues),

    validateCsvData: (sessionId) =>
      handleApiCall('validateCsvData', csvImportApi.validateCsvData.bind(csvImportApi), sessionId),

    getValidationResults: (sessionId) =>
      handleApiCall('getValidationResults', csvImportApi.getValidationResults.bind(csvImportApi), sessionId),

    getValidationProgress: (sessionId) =>
      handleApiCall('getValidationProgress', csvImportApi.getValidationProgress.bind(csvImportApi), sessionId),

    correctValidationErrors: (sessionId, corrections) =>
      handleApiCall('correctValidationErrors', csvImportApi.correctValidationErrors.bind(csvImportApi), sessionId, corrections),

    getFieldOptions: (sessionId, fieldName) =>
      handleApiCall('getFieldOptions', csvImportApi.getFieldOptions.bind(csvImportApi), sessionId, fieldName),

    detectDuplicates: (sessionId) =>
      handleApiCall('detectDuplicates', csvImportApi.detectDuplicates.bind(csvImportApi), sessionId),

    getDuplicateFields: (sessionId) =>
      handleApiCall('getDuplicateFields', csvImportApi.getDuplicateFields.bind(csvImportApi), sessionId),

    resolveDuplicates: (sessionId, resolutions) =>
      handleApiCall('resolveDuplicates', csvImportApi.resolveDuplicates.bind(csvImportApi), sessionId, resolutions),

    getPreviewData: (sessionId) =>
      handleApiCall('getPreviewData', csvImportApi.getPreviewData.bind(csvImportApi), sessionId),

    importCsvData: (sessionId) =>
      handleApiCall('importCsvData', csvImportApi.importCsvData.bind(csvImportApi), sessionId),

    getImportProgress: (sessionId) =>
      handleApiCall('getImportProgress', csvImportApi.getImportProgress.bind(csvImportApi), sessionId),

    cancelImport: (sessionId) =>
      handleApiCall('cancelImport', csvImportApi.cancelImport.bind(csvImportApi), sessionId),

    getImportSummary: (sessionId) =>
      handleApiCall('getImportSummary', csvImportApi.getImportSummary.bind(csvImportApi), sessionId),

    downloadErrorReport: (sessionId) =>
      handleApiCall('downloadErrorReport', csvImportApi.downloadErrorReport.bind(csvImportApi), sessionId),

    downloadErrorCsv: (sessionId) =>
      handleApiCall('downloadErrorCsv', csvImportApi.downloadErrorCsv.bind(csvImportApi), sessionId),

    getSystemFields: (entityType) =>
      handleApiCall('getSystemFields', csvImportApi.getSystemFields.bind(csvImportApi), entityType),
  }), [context, loading, errors, handleApiCall]);

  return apiOperations;
};
